import { Link } from 'react-router-dom';
import { ShieldCheck } from 'lucide-react';
import { SEO } from '@/components/landing/shared/SEO';
import { cn } from '@/lib/utils';

const SecurityPrivacyPage = () => {
    return (
        <div className={cn(
            "relative min-h-screen bg-background overflow-hidden selection:bg-primary/20 selection:text-foreground",
            "pt-32 pb-20"
        )}>
            <SEO
                title="Security & Privacy - Enterprise Data Protection"
                description="Enterprise-grade security for your AI chatbot data. GDPR & CCPA compliant. Data encryption, retention policies, and user rights for US businesses."
                url="https://chytbot.com/security-privacy"
                keywords={[
                    "chatbot security",
                    "AI data privacy",
                    "GDPR compliance chatbot",
                    "CCPA compliant chatbot",
                    "enterprise security",
                    "data protection",
                    "secure chatbot platform",
                    "business data privacy USA",
                    "ChytBot security"
                ]}
            />

            <div className="absolute top-0 left-0 w-full h-[600px] bg-gradient-to-b from-primary/5 via-background to-transparent pointer-events-none -z-10" />

            <div className="container relative px-4 mx-auto max-w-4xl">
                <header className="mb-16">
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/20 text-primary text-[10px] font-bold uppercase tracking-widest mb-6">
                        <ShieldCheck className="w-3 h-3" />
                        Enterprise
                    </div>
                    <h1 className="font-display text-2xl sm:text-3xl md:text-4xl lg:text-6xl font-bold tracking-tighter text-foreground mb-4">
                        Security & Privacy
                    </h1>
                    <p className="text-sm sm:text-base md:text-lg text-muted-foreground max-w-2xl">
                        Information for enterprise customers on data handling, user rights, compliance, and how we use cookies.
                    </p>
                </header>

                <div className="prose dark:prose-invert max-w-none space-y-8 sm:space-y-12 lg:space-y-16">
                    <section>
                        <h2 className="text-2xl font-bold text-foreground mb-4">Data Retention and Sharing</h2>
                        <p className="text-muted-foreground leading-relaxed mb-4">
                            We retain data only as long as necessary to provide the service and fulfill legal obligations. Chat and conversation data are stored in secure, region-specific infrastructure. We do not sell your personal information or your customers' data.
                        </p>
                        <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                            <li><strong>Account and usage data:</strong> Retained while your account is active and for a limited period after closure for legal and support purposes.</li>
                            <li><strong>Chat and knowledge base content:</strong> Stored to power your chatbots; you can delete documents and data at any time.</li>
                            <li><strong>Sharing:</strong> We share data only with service providers necessary to operate ChytBot (e.g. hosting, analytics) under strict agreements, or when required by law.</li>
                        </ul>
                    </section>

                    <section>
                        <h2 className="text-2xl font-bold text-foreground mb-4">User Rights</h2>
                        <p className="text-muted-foreground leading-relaxed mb-4">
                            Depending on your jurisdiction, you may have rights to access, correct, delete, or export your personal data. We support these rights in line with applicable law (e.g. GDPR, CCPA).
                        </p>
                        <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                            <li><strong>Access:</strong> Request a copy of the personal data we hold about you.</li>
                            <li><strong>Correction:</strong> Update or correct inaccurate data via your account or by contacting us.</li>
                            <li><strong>Deletion:</strong> Request deletion of your data; we will comply subject to legal retention requirements.</li>
                            <li><strong>Export:</strong> Request an export of your data in a portable format where required by law.</li>
                        </ul>
                        <p className="text-muted-foreground mt-4">
                            To exercise these rights, contact us at <a href="mailto:privacy@chytbot.com" className="text-primary hover:text-primary/80 hover:underline">privacy@chytbot.com</a>.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-2xl font-bold text-foreground mb-4">Age Restrictions</h2>
                        <p className="text-muted-foreground leading-relaxed">
                            ChytBot is intended for business use. Users must be at least 18 years of age (or the age of majority in their jurisdiction) to create an account and use the service. If you are under 18, you may not use ChytBot without the involvement of a parent or guardian. By using ChytBot, you represent that you meet these age requirements. We do not knowingly collect personal information from individuals under the applicable minimum age.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-2xl font-bold text-foreground mb-4">Legal and Compliance</h2>
                        <p className="text-muted-foreground leading-relaxed">
                            Use of ChytBot is subject to our Terms of Service and Privacy Policy (available in the site footer). We aim to design our systems and practices in line with applicable data protection and privacy laws. For enterprise agreements, security questionnaires, or compliance documentation, please contact us at <a href="mailto:enterprise@chytbot.com" className="text-primary hover:text-primary/80 hover:underline">enterprise@chytbot.com</a>.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-2xl font-bold text-foreground mb-4">Cookies and Similar Technologies</h2>
                        <p className="text-muted-foreground leading-relaxed mb-4">
                            We use cookies and similar technologies to provide, secure, and improve the service.
                        </p>
                        <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                            <li><strong>Essential cookies (required):</strong> Necessary for authentication, session management, and security. These cannot be disabled if you use the product.</li>
                            <li><strong>Preference cookies:</strong> Remember your settings (e.g. theme, language) for a better experience.</li>
                            <li><strong>Analytics cookies (optional):</strong> Help us understand how the product is used so we can improve performance and features. You can opt out via cookie preferences where offered.</li>
                        </ul>
                        <p className="text-muted-foreground mt-4">
                            For more detail, see our Cookie Policy in the site footer. Questions: <a href="mailto:support@chytbot.com" className="text-primary hover:text-primary/80 hover:underline">support@chytbot.com</a>.
                        </p>
                    </section>
                </div>

                <div className="mt-16 pt-8 border-t border-border">
                    <Link
                        to="/contact"
                        className="text-sm font-medium text-primary hover:text-primary/80 hover:underline"
                    >
                        Contact us for enterprise and security inquiries →
                    </Link>
                </div>
            </div>
        </div>
    );
};

export default SecurityPrivacyPage;
